'use client';

import React, { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import MessageList from './MessageList';
import MessageInput from './MessageInput';
import Message, { MessageRef } from './Message';

interface MessageData {
  id?: number;
  role: 0 | 1;
  content: string;
  timestamp?: string;
  isComplete?: boolean;
  notification_id?: number;
}

interface ChatProps {
  conversationId: string | null;
  messageRefs: React.MutableRefObject<Map<number, MessageRef>>;
  setQuoteProgress: (progress: number) => void;
  headerTitle?: string;
  headerSubtitle?: string;
  showHeader?: boolean;
  onConversationCreated?: (id: string) => void;
  inputPlaceholder?: string;
  inputPlaceholderWaiting?: string;
  inputButtonText?: string;
  inputHelperText?: string;
  lpSlug?: string;
}

const Chat: React.FC<ChatProps> = ({
  conversationId,
  messageRefs,
  setQuoteProgress,
  headerTitle = 'Ile kosztuje Twoja sprawa prawna?',
  headerSubtitle = 'Napisz i sprawdź bez zobowiązań w kilka minut.',
  showHeader = true,
  onConversationCreated,
  inputPlaceholder,
  inputPlaceholderWaiting,
  inputButtonText,
  inputHelperText,
  lpSlug,
}) => {
  const [currentConversationId, setCurrentConversationId] = useState<string | null>(conversationId);
  const [messages, setMessages] = useState<MessageData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [isConversationClosed, setIsConversationClosed] = useState(false);
  const [chatActivated, setChatActivated] = useState(!!conversationId);

  const abortControllerRef = useRef<AbortController | null>(null);
  const streamInProgressRef = useRef(false);
  const justCreatedConversationRef = useRef(false);

  const API_URL = process.env['NEXT_PUBLIC_API_URL'] ?? '';

  // Update currentConversationId when conversationId prop changes
  useEffect(() => {
    setCurrentConversationId(conversationId);
  }, [conversationId]);

  useEffect(() => {
    // Skip fetching if we just created the conversation
    if (justCreatedConversationRef.current) {
      justCreatedConversationRef.current = false;
      return;
    }

    // Fetch welcome message if no conversationId (new conversation)
    if (!currentConversationId) {
      const fetchWelcomeMessage = async () => {
        try {
          const response = await fetch(`${API_URL}/api/welcome-message`);
          if (response.ok) {
            const welcomeMsg = await response.json();
            setMessages([welcomeMsg]);
          }
        } catch (err) {
          console.error('Failed to fetch welcome message:', err);
        } finally {
          setIsLoading(false);
        }
      };
      fetchWelcomeMessage();
      return;
    }

    const fetchHistory = async () => {
      try {
        const response = await fetch(
          `${API_URL}/api/chat/${currentConversationId}/history`
        );

        if (!response.ok) throw new Error('Failed to fetch history');

        const history = await response.json();

        // Clean up QUOTE_PROGRESS markers from history and extract latest progress
        let latestProgress: number | null = null;
        let conversationClosed = false;
        const cleanedHistory = history.map((msg: MessageData & { content: string }) => {
          if (msg.content) {
            // Check for conversation closed marker
            if (msg.content.includes('[CONVERSATION_CLOSED]')) {
              conversationClosed = true;
              msg.content = msg.content.replace(/\[CONVERSATION_CLOSED\]/g, '').trim();
            }

            // Find all progress markers
            const progressMatches = msg.content.match(/\[QUOTE_PROGRESS:(\d+)\]/g);
            if (progressMatches && progressMatches.length > 0) {
              const lastMatch = progressMatches[progressMatches.length - 1];
              if (lastMatch) {
                const progressValue = lastMatch.match(/\[QUOTE_PROGRESS:(\d+)\]/);
                if (progressValue?.[1]) {
                  latestProgress = parseInt(progressValue[1], 10);
                }
              }
            }
            msg.content = msg.content.replace(/\[QUOTE_PROGRESS:\d+\]/g, '').trim();
          }
          return msg;
        });

        if (conversationClosed) {
          setIsConversationClosed(true);
        }

        if (latestProgress !== null && setQuoteProgress) {
          setQuoteProgress(latestProgress);
        } else if (setQuoteProgress) {
          try {
            const statusResponse = await fetch(
              `${API_URL}/api/chat/${currentConversationId}/quote-progress`
            );
            if (statusResponse.ok) {
              const statusData = await statusResponse.json();
              if (statusData.progress !== undefined) {
                setQuoteProgress(statusData.progress);
              }
            }
          } catch (err) {
            console.error('Failed to fetch quote progress:', err);
          }
        }

        setMessages(cleanedHistory);
        setIsLoading(false);
      } catch (err) {
        setError((err as Error).message);
        setIsLoading(false);
      }
    };

    fetchHistory();
  }, [currentConversationId, setQuoteProgress, API_URL]);

  const handleSendMessage = useCallback(
    async (messageText: string) => {
      if (streamInProgressRef.current) {
        return;
      }

      // Activate chat on first message send
      if (!chatActivated) {
        setChatActivated(true);
      }

      try {
        streamInProgressRef.current = true;
        setIsWaitingForResponse(true);
        setError(null);

        let activeConversationId = currentConversationId;

        // Create conversation if needed (first message)
        if (!activeConversationId) {
          console.log('Creating new conversation...');
          const createResponse = await fetch(`${API_URL}/api/conversations`, {
            method: 'POST',
            credentials: 'include',
          });

          if (!createResponse.ok) {
            throw new Error('Failed to create conversation');
          }

          const data = await createResponse.json();
          activeConversationId = data.conversation_id;
          console.log('Created conversation:', activeConversationId);

          // Update URL without page reload
          const newPath = lpSlug
            ? `/${lpSlug}/chat/${activeConversationId}`
            : `/chat/${activeConversationId}`;
          window.history.pushState({}, '', newPath);

          justCreatedConversationRef.current = true;
          setCurrentConversationId(activeConversationId);

          if (onConversationCreated && activeConversationId) {
            onConversationCreated(activeConversationId);
          }
        }

        const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

        // Add user message
        setMessages((prev) => [
          ...prev,
          {
            role: 0,
            content: messageText,
            timestamp: new Date().toISOString(),
            isComplete: true,
          },
        ]);

        // Add empty assistant message
        setMessages((prev) => [
          ...prev,
          {
            role: 1,
            content: '',
            timestamp: new Date().toISOString(),
            isComplete: false,
          },
        ]);

        let accumulatedContent = '';

        const response = await fetch(
          `${API_URL}/api/chat/${activeConversationId}/stream`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              message: messageText,
              timezone: userTimezone,
            }),
          }
        );

        if (!response.ok) {
          throw new Error('Failed to get response from server');
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.trim()) continue;

            try {
              const parsed = JSON.parse(line);

              if (parsed.event === 'complete') {
                const cleanedContent = accumulatedContent
                  .replace(/\[CONVERSATION_CLOSED\]/g, '')
                  .replace(/\[QUOTE_PROGRESS:\d+\]/g, '')
                  .trim();

                setMessages((prev) =>
                  prev.map((msg, i) =>
                    i === prev.length - 1
                      ? {
                          ...msg,
                          content: cleanedContent,
                          isComplete: true,
                        }
                      : msg
                  )
                );
                streamInProgressRef.current = false;
                setIsWaitingForResponse(false);
                break;
              } else if (parsed.data) {
                // Check for conversation closed marker
                if (parsed.data.includes('[CONVERSATION_CLOSED]')) {
                  setIsConversationClosed(true);
                  parsed.data = parsed.data
                    .replace(/\[CONVERSATION_CLOSED\]/g, '')
                    .trim();
                }

                // Check for quote progress marker
                const progressMatch = parsed.data.match(
                  /\[QUOTE_PROGRESS:(\d+)\]/
                );
                if (progressMatch) {
                  const progress = parseInt(progressMatch[1], 10);
                  if (setQuoteProgress) {
                    setQuoteProgress(progress);

                    // Send GA4 event when lead is submitted (progress = 100%)
                    if (progress === 100 && typeof window !== 'undefined') {
                      const url = new URL(window.location.href);
                      const gclid = url.searchParams.get('gclid');
                      const eventId =
                        crypto?.randomUUID?.() ||
                        `${Date.now()}-${Math.random()}`;

                      const eventData = {
                        event: 'lead_submit',
                        event_id: eventId,
                        lead_type: 'formularz_kontaktowy',
                        form_id: 'lead-form-1',
                        value: 0,
                        currency: 'PLN',
                        gclid: gclid || undefined,
                      };

                      console.log('GA4 Lead Event:', eventData);

                      (window as Window & { dataLayer?: unknown[] }).dataLayer =
                        (window as Window & { dataLayer?: unknown[] })
                          .dataLayer || [];
                      (
                        window as Window & { dataLayer?: unknown[] }
                      ).dataLayer!.push(eventData);
                    }
                  }
                  parsed.data = parsed.data
                    .replace(/\[QUOTE_PROGRESS:\d+\]/g, '')
                    .trim();
                }

                if (parsed.data) {
                  accumulatedContent += parsed.data;

                  setMessages((prev) =>
                    prev.map((msg, i) =>
                      i === prev.length - 1
                        ? {
                            ...msg,
                            content: accumulatedContent,
                            isComplete: false,
                          }
                        : msg
                    )
                  );
                }
              }
            } catch {
              console.warn('Failed to parse line:', line);
            }
          }
        }
      } catch (err) {
        if ((err as Error).name === 'AbortError') {
          console.log('Stream aborted');
          return;
        }
        console.error('Error in handleSendMessage:', err);
        setError((err as Error).message);
      } finally {
        streamInProgressRef.current = false;
        setIsWaitingForResponse(false);
      }
    },
    [
      currentConversationId,
      setQuoteProgress,
      onConversationCreated,
      chatActivated,
      API_URL,
      lpSlug,
    ]
  );

  const handleButtonClick = useCallback(
    (buttonText: string) => {
      handleSendMessage(buttonText);
    },
    [handleSendMessage]
  );

  const handleInputActivate = useCallback(() => {
    if (!chatActivated) {
      setChatActivated(true);
    }
  }, [chatActivated]);

  // Memoize filtered messages to prevent unnecessary recalculations
  const displayMessages = useMemo(() => {
    if (chatActivated) {
      return messages;
    }
    return messages.slice(1);
  }, [messages, chatActivated]);

  // Memoize first message for non-activated chat
  const firstMessage = useMemo((): MessageData | null => {
    if (!chatActivated && messages.length > 0) {
      const first = messages[0];
      if (first && first.role === 1) {
        return first;
      }
    }
    return null;
  }, [messages, chatActivated]);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <div className="flex-1 flex flex-col h-full bg-transparent w-full relative z-10">
      <div className="flex-1 flex flex-col h-full relative">
        {/* Header text */}
        {showHeader && (
          <div className="px-6 pt-6 pb-2 text-left">
            <h2
              className={`font-display text-lawmingo-yellow leading-[120%] mb-6 transition-all duration-300 ${
                chatActivated ? 'text-2xl' : 'text-2xl lg:text-52'
              }`}
            >
              {headerTitle}
            </h2>
            <p
              className={`font-display text-lawmingo-cream transition-all duration-300 ${
                chatActivated ? 'text-sm' : 'text-base lg:text-lg'
              }`}
            >
              {headerSubtitle}
            </p>
          </div>
        )}

        <div className="flex-1 lg:min-h-0 overflow-y-auto flex flex-col">
          {isLoading ? (
            <div
              className="flex h-full items-center justify-center"
              role="status"
              aria-label="Ladowanie rozmowy"
            >
              <div className="text-center">
                <div
                  className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-accent-600 mx-auto"
                  aria-hidden="true"
                ></div>
                <p className="mt-4 text-lawmingo-cream/70">
                  Ladowanie rozmowy...
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Regular messages */}
              <MessageList
                messages={displayMessages}
                messageRefs={messageRefs}
                onButtonClick={handleButtonClick}
              />

              {/* First message at bottom with blur/shadow */}
              {firstMessage && (
                <div className="mt-auto">
                  <Message
                    message={firstMessage as MessageData & { role: 0 | 1 }}
                    isLast={messages.length === 1}
                    onButtonClick={handleButtonClick}
                    withBlurShadow={true}
                  />
                </div>
              )}
            </>
          )}
        </div>

        <div className="bg-transparent px-6 py-4 mb-8 z-[60]">
          {isConversationClosed ? (
            <div className="text-center" role="status" aria-live="polite">
              <a
                href="/"
                className="inline-flex items-center justify-center px-6 py-3 text-lg font-medium rounded-full bg-accent-600 text-gray-900 hover:bg-accent-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent-500 transition-colors"
                aria-label="Otworz nowy czat"
              >
                Otworz nowy czat
              </a>
            </div>
          ) : (
            <>
              <MessageInput
                onSend={handleSendMessage}
                disabled={
                  isLoading ||
                  streamInProgressRef.current ||
                  !!error ||
                  isConversationClosed
                }
                error={error}
                isWaitingForResponse={isWaitingForResponse}
                onActivate={handleInputActivate}
                {...(inputPlaceholder !== undefined && { placeholder: inputPlaceholder })}
                {...(inputPlaceholderWaiting !== undefined && { placeholderWaiting: inputPlaceholderWaiting })}
                {...(inputButtonText !== undefined && { buttonText: inputButtonText })}
                showButtonAsArrow={!!currentConversationId}
              />
              {inputHelperText && (
                <p className="text-center text-lawmingo-cream/60 text-sm mt-3">
                  {inputHelperText}
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(Chat);
